/**
 * tooltipPosition: decide where a hover tooltip goes so it is actually visible.
 *
 * Works in VIEWPORT coordinates on purpose. The tooltip used to be positioned
 * relative to `.oq-sidebar` and portaled into it, and since that container is
 * `overflow: hidden` (styles/blocks/_sidebar.css) anything computed outside it
 * was silently discarded. Measured 2026-08-05: on the 64px collapsed rail all
 * six nav tooltips rendered 7-11% visible, and that rail is icon-only, so the
 * tooltip was the only label those icons had.
 *
 * Two rules, in order:
 *   1. FLIP. If the preferred side has no room, use the opposite side. This is
 *      the one that matters for a right-docked rail: "right" has nothing but
 *      screen edge, "left" has the whole page.
 *   2. CLAMP. Whatever side wins, keep the box inside the viewport.
 *
 * Clamping alone would not do: on the collapsed rail there is no room on the
 * right at any offset, so a clamp would just park the tooltip on the screen
 * edge, on top of the icon it describes.
 *
 * The resolved placement is returned because the caller needs it for the arrow
 * class — a flipped tooltip with an unflipped arrow points at nothing.
 */

export type TooltipPlacement = 'top' | 'right' | 'bottom' | 'left';

export interface TooltipRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface TooltipSize {
  width: number;
  height: number;
}

export interface TooltipViewport {
  width: number;
  height: number;
}

export interface TooltipPositionInput {
  /** Trigger box in viewport coordinates. */
  trigger: TooltipRect;
  /** Measured tooltip box. */
  tooltip: TooltipSize;
  placement: TooltipPlacement;
  viewport: TooltipViewport;
  /** Space between trigger and tooltip. */
  gap?: number;
  /** Minimum breathing room against the viewport edge. */
  margin?: number;
}

export interface TooltipPositionResult {
  top: number;
  left: number;
  /** Placement after any flip. Drives the arrow class. */
  placement: TooltipPlacement;
}

const DEFAULT_GAP = 6;
const DEFAULT_MARGIN = 4;

const OPPOSITE: Record<TooltipPlacement, TooltipPlacement> = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
};

/** Room between the trigger and the viewport edge on the given side. */
function spaceOn(placement: TooltipPlacement, trigger: TooltipRect, viewport: TooltipViewport): number {
  switch (placement) {
    case 'top':
      return trigger.top;
    case 'bottom':
      return viewport.height - (trigger.top + trigger.height);
    case 'left':
      return trigger.left;
    case 'right':
      return viewport.width - (trigger.left + trigger.width);
  }
}

/** Size the tooltip needs on the placement axis, including the gap. */
function neededOn(placement: TooltipPlacement, tooltip: TooltipSize, gap: number): number {
  return (placement === 'top' || placement === 'bottom' ? tooltip.height : tooltip.width) + gap;
}

function clamp(value: number, min: number, max: number): number {
  // max < min when the tooltip is larger than the space it must fit into.
  // Prefer the near edge over a negative coordinate: on-screen and awkward
  // beats off-screen and invisible.
  if (max < min) return min;
  return Math.max(min, Math.min(value, max));
}

export function computeTooltipPosition({
  trigger,
  tooltip,
  placement,
  viewport,
  gap = DEFAULT_GAP,
  margin = DEFAULT_MARGIN,
}: TooltipPositionInput): TooltipPositionResult {
  // 1. FLIP when the preferred side cannot hold the tooltip.
  const opposite = OPPOSITE[placement];
  const preferredSpace = spaceOn(placement, trigger, viewport);
  const oppositeSpace = spaceOn(opposite, trigger, viewport);
  const needed = neededOn(placement, tooltip, gap) + margin;

  let resolved = placement;
  if (preferredSpace < needed) {
    // Take the opposite side if it fits, or if it is simply roomier. Falling
    // back to "roomier" keeps the tooltip off the edge it is already jammed
    // against when neither side can hold it outright.
    if (oppositeSpace >= needed || oppositeSpace > preferredSpace) {
      resolved = opposite;
    }
  }

  // 2. Position on the resolved side, centred across the other axis.
  let top: number;
  let left: number;
  switch (resolved) {
    case 'top':
      top = trigger.top - tooltip.height - gap;
      left = trigger.left + trigger.width / 2 - tooltip.width / 2;
      break;
    case 'bottom':
      top = trigger.top + trigger.height + gap;
      left = trigger.left + trigger.width / 2 - tooltip.width / 2;
      break;
    case 'left':
      top = trigger.top + trigger.height / 2 - tooltip.height / 2;
      left = trigger.left - tooltip.width - gap;
      break;
    case 'right':
      top = trigger.top + trigger.height / 2 - tooltip.height / 2;
      left = trigger.left + trigger.width + gap;
      break;
  }

  // 3. CLAMP both axes into the viewport.
  return {
    placement: resolved,
    left: clamp(left, margin, viewport.width - tooltip.width - margin),
    top: clamp(top, margin, viewport.height - tooltip.height - margin),
  };
}
